import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FolderPlus, Loader2, X } from 'lucide-react';

export default function CreateFolderModal({
    activeFolderId,
    activeFolderName,
    canCreateInsideActiveFolder,
    errorMessage,
    isSubmitting,
    onClose,
    onSubmit,
    scopeLabel,
}) {
    const [name, setName] = useState('');
    const [createInsideParent, setCreateInsideParent] = useState(Boolean(activeFolderId) && canCreateInsideActiveFolder);

    useEffect(() => {
        setCreateInsideParent(Boolean(activeFolderId) && canCreateInsideActiveFolder);
    }, [activeFolderId, canCreateInsideActiveFolder]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    const parentLabel = useMemo(() => {
        if (!activeFolderId) return null;
        return activeFolderName || 'selected folder';
    }, [activeFolderId, activeFolderName]);

    const handleSubmit = (event) => {
        event.preventDefault();
        const trimmedName = name.trim();
        if (!trimmedName || isSubmitting) return;

        onSubmit({
            name: trimmedName,
            parentFolderId: createInsideParent && canCreateInsideActiveFolder ? activeFolderId : null,
        });
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm" onClick={handleBackdropClick}>
            <div className="relative w-full max-w-md rounded-2xl border border-[#2A2B32] bg-[#1E1F25] p-6 shadow-2xl shadow-black/60">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-4 top-4 rounded-md p-1 text-gray-400 transition hover:bg-[#2A2B32] hover:text-gray-200"
                    aria-label="Close create folder modal"
                >
                    <X size={18} />
                </button>

                <div className="mb-5 flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-indigo-500/20 bg-indigo-500/10 text-indigo-300">
                        <FolderPlus size={18} />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-100">Create folder</h2>
                        <p className="mt-1 text-sm text-gray-400">
                            Add a new folder for {scopeLabel.toLowerCase()} items.
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="folder-name" className="text-sm text-gray-300">
                            Folder name
                        </label>
                        <input
                            id="folder-name"
                            type="text"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            placeholder="e.g. System Design"
                            autoFocus
                            className="w-full rounded-lg border border-[#373F54] bg-[#2A2B32] px-3 py-2 text-gray-200 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30"
                        />
                    </div>

                    {activeFolderId && canCreateInsideActiveFolder && (
                        <label className="flex items-start gap-3 rounded-xl border border-[#31384C] bg-[#171920] px-3 py-3 text-sm text-gray-300">
                            <input
                                type="checkbox"
                                checked={createInsideParent}
                                onChange={(event) => setCreateInsideParent(event.target.checked)}
                                className="mt-1 h-4 w-4 rounded border-gray-600 bg-transparent text-indigo-500 focus:ring-indigo-500"
                            />
                            <span>
                                Create this folder inside <span className="font-medium text-gray-100">{parentLabel}</span>
                            </span>
                        </label>
                    )}

                    {activeFolderId && !canCreateInsideActiveFolder && (
                        <p className="rounded-xl border border-[#31384C] bg-[#171920] px-3 py-3 text-sm text-gray-400">
                            Folders can only be nested one level deep. This folder will be created at the top level.
                        </p>
                    )}

                    {errorMessage && (
                        <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                            {errorMessage}
                        </div>
                    )}

                    <div className="mt-2 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg border border-[#3A4257] px-4 py-2 text-sm font-medium text-gray-300 transition hover:bg-[#2B3242]"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || !name.trim()}
                            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {isSubmitting && <Loader2 size={15} className="animate-spin" />}
                            {isSubmitting ? 'Creating...' : 'Create folder'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

CreateFolderModal.propTypes = {
    activeFolderId: PropTypes.string,
    activeFolderName: PropTypes.string,
    canCreateInsideActiveFolder: PropTypes.bool,
    errorMessage: PropTypes.string,
    isSubmitting: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    scopeLabel: PropTypes.string.isRequired,
};

CreateFolderModal.defaultProps = {
    activeFolderId: null,
    activeFolderName: null,
    canCreateInsideActiveFolder: true,
    errorMessage: null,
};
